import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { AlertButton } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AUTH_TRANSACTION_CODES,
	AUTH_TRANSACTION_MESSAGE_CONTEXT,
	AuthTransactionAction,
	CoreState,
	Jurisdiction,
	LoginMethod,
	MessageService,
	PERSONAL_NUMBER_CODES,
	SessionSelector,
	VerificationStatus,
	VerificationStatusRequest,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { RouteSelector } from "@obg/ngx.router";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { EMPTY, merge, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import { CommonAuthTelemetryEventName, CommonAuthTelemetryVerificationStatus } from "../../../sdk";
import { LoginUiFlowService } from "../../login-ui/login-ui-flow.service";
import { SgaLoginUiFlowService } from "../sga-login-flow.service";
import { SGA_LOGIN_TRANSACTION_PREFIX } from "../sga-login.const";
import { SgaLoginData, SgaLoginError } from "../sga-login.model";

const SGA_LOGIN_FORM_ERROR_ALERT_ID = "SGA_LOGIN_FORM_ERROR_ALERT_ID";

@Component({
	selector: "obg-m-sga-login-form-container",
	templateUrl: "./sga-login-form.container.html",
	styleUrls: ["./sga-login-form.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SgaLoginFormContainer implements OnInit, OnDestroy, OnChanges {
	@HostBinding("class.obg-m-sga-login-form") hostCssClass = true;

	@Input() submitBtnKey: string | undefined;
	@Input() set translationPrefix(value: string | undefined) {
		if (value) {
			this.notificationAlertTitle = `${value}.notification.title`;
		}
		this._translationPrefix = value;
	}
	get translationPrefix(): string | undefined {
		return this._translationPrefix;
	}
	@Input() personalNumber: string | undefined;

	@Output() busy = new EventEmitter<boolean>();
	@Output() loginSubmit = new EventEmitter<SgaLoginData>();

	polling = false;
	errorMessage = "";
	customerCanLogInAt: string | undefined;
	showError = false;
	personalNumberForm: UntypedFormGroup;
	isVerificationSuccess = false;

	private readonly isDesktop: boolean;

	private data$$: Subscription | undefined;
	private sgaPersonalNumberMessages: (() => void) | undefined;
	private isStatusPending = false;
	private loginToken: string | undefined;
	private isVerificationAborted = false;
	private closeButton: AlertButton = {
		text: "general.close",
		handler: () => this.enableFormInteractions(),
	};
	private notificationAlertTitle: string | undefined;
	private _translationPrefix: string | undefined;

	constructor(
		private sessionSelector: SessionSelector,
		private store: Store<CoreState>,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private authTransactionAction: AuthTransactionAction,
		private alertService: AbstractTranslatableAlertService,
		private formBuilder: UntypedFormBuilder,
		private sgaLoginUiFlowService: SgaLoginUiFlowService,
		private telemetryService: TelemetryService,
		deviceConfigService: DeviceConfigService,
		private loginUiFlowService: LoginUiFlowService,
		private customerSupportService: CustomerSupportService
	) {
		this.personalNumberForm = this.formBuilder.group({
			personalNumber: this.formBuilder.control("", [
				Validators.required,
				Validators.minLength(10),
				Validators.maxLength(13),
				OBG_VALIDATORS.nationalId(Jurisdiction.Sga),
			]),
			shouldRememberUser: false,
		});
		this.isDesktop = deviceConfigService.isDesktop();
	}

	ngOnInit() {
		const personalNumberControl = this.personalNumberForm.controls.personalNumber;

		const customerData$ = this.store.pipe(
			select(this.sessionSelector.getCustomerNotVerifiedData()),
			ignoreNil(),
			tap((data) => {
				this.loginToken = data.token;
				if (data.personalNumber && !personalNumberControl.value) {
					personalNumberControl.setValue(data.personalNumber);
					personalNumberControl.disable();
				}
			})
		);

		const personalNumberChanges$ = personalNumberControl.valueChanges.pipe(
			filter(() => personalNumberControl.valid),
			take(1),
			tap(() => personalNumberControl.markAsTouched())
		);

		const personalNumberErrorCloseButton = {
			text: "general.close",
			handler: () => {
				personalNumberControl.updateValueAndValidity();
				this.enableFormInteractions();
				this.showError = true;
			},
		};

		const personalNumberErrors$ = this.sgaLoginUiFlowService.personalNumberErrors$().pipe(
			tap((error) =>
				this.handleError({
					message: `${this.translationPrefix}.${error}`.toLowerCase(),
				})
			)
		);

		const validationErrorInvalidChecksum$ = this.messageService
			.consume$({
				messageCode: PERSONAL_NUMBER_CODES.validationErrorInvalidCheckSum,
			})
			.pipe(
				tap(() => {
					this.errorMessage =
						`${this.translationPrefix}.${PERSONAL_NUMBER_CODES.validationErrorInvalidCheckSum}`.toLowerCase();
					this.handleError({
						message: this.errorMessage,
						buttons: [
							personalNumberErrorCloseButton,
							{
								text: "general.contact",
								handler: () => this.customerSupportService.openSupportPageInNewTab(),
							},
						],
					});
				})
			);

		const validationErrorInvalidDate$ = this.messageService
			.consume$({
				messageCode: PERSONAL_NUMBER_CODES.validationErrorInvalidDate,
			})
			.pipe(
				tap(() => {
					this.errorMessage =
						`${this.translationPrefix}.${PERSONAL_NUMBER_CODES.validationErrorInvalidDate}`.toLowerCase();
					this.handleError({
						message: this.errorMessage,
						buttons: [
							personalNumberErrorCloseButton,
							{
								text: "general.contact",
								handler: () => this.customerSupportService.openSupportPageInNewTab(),
							},
						],
					});
				})
			);

		const validationErrorPersonalNumberUnderage$ = this.messageService
			.consume$({
				messageCode: PERSONAL_NUMBER_CODES.validationErrorPersonalNumberUnderage,
			})
			.pipe(
				tap(() =>
					this.handleError({
						message:
							`${this.translationPrefix}.${PERSONAL_NUMBER_CODES.validationErrorPersonalNumberUnderage}`.toLowerCase(),
						buttons: [personalNumberErrorCloseButton],
					})
				)
			);

		const loginTransactionsVerificationStatus$ = this.sgaLoginUiFlowService
			.loginTransactionsVerificationStatus$()
			.pipe(
				tap((status) => {
					if (status === VerificationStatus.Pending) {
						this.isStatusPending = true;
						this.showPolling();
						return;
					}

					if (status === VerificationStatus.Completed) {
						this.isVerificationSuccess = true;

						this.telemetryService.track({
							event: CommonAuthTelemetryEventName.BankIDVerification,
							LoginMethod: LoginMethod.BankId,
							Status: CommonAuthTelemetryVerificationStatus.Success,
						});
					}

					this.enableFormInteractions();
					this.isStatusPending = false;
				})
			);

		const initializeVerificationFail$ = this.sgaLoginUiFlowService.initializeVerificationFail$().pipe(
			tap(() => {
				this.handleError({
					message: "sga-login-transaction.initialize.fail",
					buttons: [
						{
							text: "general.ok",
						},
					],
				});
				this.isVerificationAborted = true;
				this.enableFormInteractions();
			})
		);

		const personalNumberAlreadyUsed$ = this.messageService
			.consume$({
				context: AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				messageCode: AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyUsed,
			})
			.pipe(
				tap(() => {
					this.handleError({
						message:
							`${SGA_LOGIN_TRANSACTION_PREFIX}.${AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyUsed}`.toLowerCase(),
					});
					this.isVerificationAborted = true;
				})
			);

		const customerVerificationFail$ = this.sgaLoginUiFlowService.customerVerificationFail$().pipe(
			tap(() => {
				this.handleError({
					message: `${this.translationPrefix}.error.text`,
				});
				this.isVerificationAborted = true;
			})
		);

		const customerVerificationCancel$ = this.sgaLoginUiFlowService.customerVerificationCancel$().pipe(
			filter(() => this.polling && !this.isVerificationAborted),
			tap(() => {
				this.showVerificationCancelled();
				this.enableFormInteractions();
			})
		);

		const invalidCredentialsError$ = this.sgaLoginUiFlowService
			.invalidCredentialsError$()
			.pipe(tap(() => this.loginFormErrorHandler("sga-login-transaction.error.personal-number-not-found")));

		const genericLoginError$ = this.sgaLoginUiFlowService
			.genericLoginError$()
			.pipe(
				tap((error) =>
					this.loginFormErrorHandler(`${this.translationPrefix}.${error || "error.text"}`.toLowerCase())
				)
			);

		const sessionLimitExpired$ = this.loginUiFlowService.sessionLimitExpired$().pipe(
			tap((error) => {
				this.customerCanLogInAt = error.customerCanLogInAt;
				this.loginFormErrorHandler(`${this.translationPrefix}.${error?.code || "error.text"}`.toLowerCase());
			})
		);

		const openBankIdApp$ = !this.isDesktop
			? this.sgaLoginUiFlowService.openBankIdApp$(
					this.polling,
					this.isVerificationAborted,
					this.translationPrefix
				)
			: EMPTY;

		const authBusy$ = this.store.pipe(
			select(this.sessionSelector.isBusy()),
			tap((isBusy) => this.busy.emit(isBusy))
		);

		const getVerificationStatus$ = this.store
			.select<VerificationStatusRequest | undefined>(RouteSelector.getQueryParams)
			.pipe(
				filter((params) => !!(params?.personalNumber && params.transactionId && !this.polling)),
				take(1),
				tap(({ personalNumber, transactionId }: VerificationStatusRequest) => {
					personalNumberControl.setValue(personalNumber);
					this.store.dispatch(
						this.authTransactionAction.getVerificationStatus({ transactionId, personalNumber })
					);
				})
			);

		this.data$$ = merge(
			customerData$,
			personalNumberErrors$,
			getVerificationStatus$,
			initializeVerificationFail$,
			validationErrorPersonalNumberUnderage$,
			validationErrorInvalidDate$,
			validationErrorInvalidChecksum$,
			customerVerificationCancel$,
			openBankIdApp$,
			loginTransactionsVerificationStatus$,
			customerVerificationFail$,
			personalNumberChanges$,
			invalidCredentialsError$,
			authBusy$,
			genericLoginError$,
			sessionLimitExpired$,
			personalNumberAlreadyUsed$
		).subscribe(() => this.changeDetector.markForCheck());

		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.BankIDFormOpen,
			LoginMethod: LoginMethod.BankId,
		});
	}

	ngOnChanges(changes: { personalNumber?: SimpleChange }) {
		if (!changes.personalNumber) {
			return;
		}
		this.personalNumberForm.patchValue({
			personalNumber: changes.personalNumber.currentValue,
			shouldRememberUser: !!changes.personalNumber.currentValue,
		});
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();

		if (this.sgaPersonalNumberMessages) {
			this.sgaPersonalNumberMessages();
		}
		if (this.isStatusPending) {
			this.store.dispatch(this.authTransactionAction.cancelLastVerification());
		}
	}

	login() {
		const personalNumberField = this.personalNumberForm.controls.personalNumber;
		if (!personalNumberField.valid && !personalNumberField.disabled) {
			return;
		}
		this.loginSubmit.emit({
			personalNumberForm: this.personalNumberForm,
			loginToken: this.loginToken,
		});
		this.busy.emit(true);
		this.showPolling();

		this.telemetryService.track({
			LoginMethod: LoginMethod.BankId,
		});
	}

	submitCancel() {
		this.showVerificationCancelled();
		if (this.personalNumberForm.controls.personalNumber.touched && this.isStatusPending) {
			this.store.dispatch(this.authTransactionAction.cancelLastVerification());
			return;
		}

		this.enableFormInteractions();
	}

	gtmEventAction(fieldName: string) {
		this.telemetryService.track({
			event: GtmEventAction.LoginFieldChange,
			FieldName: fieldName,
			LoginMethod: LoginMethod.BankId,
		});
	}

	private showPolling() {
		this.errorMessage = "";
		this.customerCanLogInAt = "";
		this.showError = false;
		this.isVerificationAborted = false;
		this.disableFormInteractions();
	}

	private showVerificationCancelled() {
		this.isVerificationAborted = true;
		this.handleError({
			buttons: [this.closeButton],
			message: `${this.translationPrefix}.${AUTH_TRANSACTION_CODES.cancelLastVerification.success}`.toLowerCase(),
		});
	}

	private enableFormInteractions() {
		this.polling = false;
		this.personalNumberForm.controls.personalNumber.enable();
		this.busy.emit(false);
		this.changeDetector.markForCheck();
	}

	private disableFormInteractions() {
		this.polling = true;
		this.personalNumberForm.controls.personalNumber.markAsTouched();
		this.personalNumberForm.controls.personalNumber.disable();
		this.changeDetector.markForCheck();
	}

	private handleError(error: SgaLoginError) {
		if (!this.isDesktop) {
			this.alertService.open(
				{
					id: SGA_LOGIN_FORM_ERROR_ALERT_ID,
					message: error.message,
					title: error.title ? error.title : this.notificationAlertTitle,
					buttons: error.buttons
						? error.buttons
						: [
								this.closeButton,
								{
									text: "general.contact",
									handler: () => this.customerSupportService.openSupportPageInNewTab(),
								},
							],
				},
				[TranslationGroup.Common]
			);
			return;
		}
		this.loginFormErrorHandler(error.message);
	}

	private loginFormErrorHandler(errorMessage: string) {
		this.errorMessage = errorMessage;
		this.showError = true;
		this.enableFormInteractions();
		this.isVerificationSuccess = false;
	}
}
